import { useState, useEffect, useMemo } from 'react';
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc } from 'firebase/firestore';
import { db, rebuildAllSiteNamesFunction, removeFacilityFunction } from '../firebase';
import logger from '../utils/logger';
import { X, Edit2, Trash2, Lightbulb, BarChart3, Building2, Maximize2, Warehouse, HardHat, RefreshCw, Loader2, AlertTriangle, Trash } from 'lucide-react';

export default function LocationManager({ onClose, darkMode, monthlyStats = [], onRebuildComplete }) {
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    city: '',
    state: '',
    zip: '',
    aliases: '',
    squareFootage: '',
    officeSquareFootage: '',
    warehouseSquareFootage: '',
    buildingAge: '',
    notes: '',
    inactive: false,
    inactiveDate: ''
  });
  const [showTip, setShowTip] = useState(false);
  const [rebuildLoading, setRebuildLoading] = useState(false);
  const [removingFacility, setRemovingFacility] = useState(null);

  // Compute facilities that appear in stats/bills but are NOT in approved_locations
  const unmatchedFacilities = useMemo(() => {
    if (!monthlyStats || monthlyStats.length === 0) return [];
    const approvedNames = new Set(locations.map(l => l.name));
    const statFacilities = [...new Set(monthlyStats.map(s => s.facility))];
    return statFacilities
      .filter(name => name && name !== 'Unknown' && !approvedNames.has(name))
      .sort();
  }, [monthlyStats, locations]);

  useEffect(() => {
    loadLocations();
  }, []);

  const loadLocations = async () => {
    setLoading(true);
    try {
      const snapshot = await getDocs(collection(db, 'approved_locations'));
      const locs = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setLocations(locs.sort((a, b) => a.name.localeCompare(b.name)));
    } catch (error) {
      logger.error('Error loading locations:', error);
      alert('Failed to load locations');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.address) {
      alert('Name and address are required');
      return;
    }

    const locationData = {
      name: formData.name.trim(),
      address: formData.address.trim(),
      city: formData.city.trim(),
      state: formData.state.trim(),
      zip: formData.zip.trim(),
      aliases: formData.aliases.split(',').map(a => a.trim()).filter(a => a),
      squareFootage: formData.squareFootage ? parseInt(formData.squareFootage) : null,
      officeSquareFootage: formData.officeSquareFootage ? parseInt(formData.officeSquareFootage) : null,
      warehouseSquareFootage: formData.warehouseSquareFootage ? parseInt(formData.warehouseSquareFootage) : null,
      buildingAge: formData.buildingAge ? parseInt(formData.buildingAge) : null,
      notes: formData.notes.trim() || '',
      inactive: formData.inactive || false,
      inactiveDate: formData.inactive ? (formData.inactiveDate || null) : null,
      updatedAt: new Date()
    };

    try {
      if (editingId) {
        await updateDoc(doc(db, 'approved_locations', editingId), locationData);
      } else {
        await addDoc(collection(db, 'approved_locations'), {
          ...locationData,
          createdAt: new Date()
        });
      }
      
      setFormData({ name: '', address: '', city: '', state: '', zip: '', aliases: '', squareFootage: '', officeSquareFootage: '', warehouseSquareFootage: '', buildingAge: '', notes: '', inactive: false, inactiveDate: '' });
      setEditingId(null);
      loadLocations();
    } catch (error) {
      logger.error('Error saving location:', error);
      alert('Failed to save location');
    }
  };

  const handleEdit = (location) => {
    setEditingId(location.id);
    setFormData({
      name: location.name,
      address: location.address,
      city: location.city || '',
      state: location.state || '',
      zip: location.zip || '',
      aliases: (location.aliases || []).join(', '),
      squareFootage: location.squareFootage || '',
      officeSquareFootage: location.officeSquareFootage || '',
      warehouseSquareFootage: location.warehouseSquareFootage || '',
      buildingAge: location.buildingAge || '',
      notes: location.notes || '',
      inactive: location.inactive || false,
      inactiveDate: location.inactiveDate || ''
    });
  };

  const handleDelete = async (id) => {
    if (!confirm('Are you sure you want to delete this location?')) return;
    
    try {
      await deleteDoc(doc(db, 'approved_locations', id));
      loadLocations();
    } catch (error) {
      logger.error('Error deleting location:', error);
      alert('Failed to delete location');
    }
  };

  const handleCancel = () => {
    setEditingId(null);
    setFormData({ name: '', address: '', city: '', state: '', zip: '', aliases: '', squareFootage: '', officeSquareFootage: '', warehouseSquareFootage: '', buildingAge: '', notes: '', inactive: false, inactiveDate: '' });
  };

  const handleRebuildAll = async () => {
    if (!window.confirm(
      'Rebuild All Site Names?\n\n' +
      'This will re-match every active bill\'s address against the current approved locations list.\n\n' +
      '• Bills matching an approved location will be reassigned\n' +
      '• Bills with unrecognized addresses that aren\'t in approved locations will be set to "Unknown"\n' +
      '• Monthly statistics will be recalculated for all affected facilities\n\n' +
      'This may take a moment. Continue?'
    )) return;

    setRebuildLoading(true);
    try {
      const result = await rebuildAllSiteNamesFunction();
      const data = result.data;
      alert(
        `✅ Rebuild Complete\n\n` +
        `Bills scanned: ${data.totalScanned}\n` +
        `Bills updated: ${data.updatedBills}\n` +
        `Bills unchanged: ${data.unchangedBills}\n` +
        `Set to Unknown: ${data.unknownBills}\n\n` +
        `Stats deleted: ${data.deletedStats}\n` +
        `Stats rebuilt: ${data.writtenStats}`
      );
      if (onRebuildComplete) onRebuildComplete();
    } catch (error) {
      logger.error('Error rebuilding site names:', error);
      alert('Failed to rebuild site names. Please try again.');
    } finally {
      setRebuildLoading(false);
    }
  };

  const handleRemoveFacility = async (facilityName) => {
    if (!window.confirm(
      `Remove "${facilityName}"?\n\n` +
      `This will find all bills currently tagged as "${facilityName}" and reassign them:\n\n` +
      '• Bills with addresses matching an approved location will be reassigned to that location\n' +
      '• Bills with no matching address will be set to "Unknown"\n' +
      '• Monthly statistics will be recalculated\n\n' +
      'Continue?'
    )) return;

    setRemovingFacility(facilityName);
    try {
      const result = await removeFacilityFunction({ facilityName });
      const data = result.data;
      alert(
        `✅ Facility Removed\n\n` +
        `"${data.facilityRemoved}" has been removed.\n\n` +
        `Bills affected: ${data.totalBills}\n` +
        `Bills reassigned: ${data.reassignedBills}\n` +
        `Set to Unknown: ${data.unknownBills}\n\n` +
        `Stats deleted: ${data.deletedStats}\n` +
        `Stats rebuilt: ${data.writtenStats}`
      );
      if (onRebuildComplete) onRebuildComplete();
    } catch (error) {
      logger.error('Error removing facility:', error);
      alert('Failed to remove facility. Please try again.');
    } finally {
      setRemovingFacility(null);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[100] p-4">
      <div className={`rounded-xl shadow-2xl max-w-6xl w-full max-h-[90vh] overflow-hidden flex flex-col transition-colors duration-200 ${
        darkMode ? 'bg-[#121212] border border-cyan-500 shadow-[0_0_18px_rgba(14,165,233,0.175)]' : 'bg-white border border-sky-500/40 shadow-[0_4px_15px_rgba(14,165,233,0.125)]'
      }`}>
        {/* Header */}
        <div className={`px-6 py-4 flex justify-between items-center border-b transition-colors duration-200 ${
          darkMode ? 'bg-[#1a1a2e] border-cyan-500/50' : 'bg-white border-sky-400/30'
        }`}>
          <div>
            <h2 className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>Location Manager</h2>
            <p className={`text-sm mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Manage approved facility locations for auto-matching</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleRebuildAll}
              disabled={rebuildLoading}
              className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-semibold transition-all ${
                rebuildLoading
                  ? 'opacity-50 cursor-not-allowed'
                  : darkMode
                    ? 'bg-cyan-500/20 text-cyan-300 hover:bg-cyan-500/30 border border-cyan-500/40'
                    : 'bg-sky-50 text-sky-700 hover:bg-sky-100 border border-sky-400/30'
              }`}
              title="Re-match all bill addresses against approved locations"
            >
              {rebuildLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
              {rebuildLoading ? 'Rebuilding...' : 'Rebuild All Site Names'}
            </button>
            <button
              onClick={onClose}
              className={`rounded-full p-2 transition-colors ${
                darkMode ? 'text-gray-400 hover:text-white hover:bg-cyan-500/20' : 'text-gray-400 hover:text-gray-600 hover:bg-sky-50'
              }`}
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        <div className={`flex-1 min-h-0 overflow-y-auto lg:overflow-hidden p-6 ${darkMode ? 'bg-[#121212]' : 'bg-white'}`}>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
            {/* Form - independently scrollable on desktop */}
            <div className="pb-20 md:pb-0 lg:overflow-y-auto lg:max-h-[calc(90vh-11rem)]" style={{ scrollbarGutter: 'stable' }}>
              <div className={`rounded-xl border shadow-sm p-4 transition-colors duration-200 ${
                darkMode ? 'bg-[#1a1a2e] border-cyan-500/50 shadow-[0_0_10px_rgba(14,165,233,0.14)]' : 'bg-white border-sky-400/30 shadow-[0_2px_10px_rgba(14,165,233,0.075)]'
              }`}>
                <h3 className={`text-base font-semibold mb-3 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  {editingId ? 'Edit Location' : 'Add New Location'}
                </h3>
              
                <form id="location-form" onSubmit={handleSubmit} className="space-y-3">
                  <div>
                    <label className={`block text-xs font-semibold mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      Facility Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className={`w-full px-3 py-1.5 text-sm border rounded-lg transition-colors focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 ${
                        darkMode ? 'bg-[#121212] border-cyan-500/50 text-white placeholder-gray-500' : 'bg-white border-sky-400/40 text-gray-900'
                      }`}
                      placeholder="e.g., Pengate - Chalfont"
                      required
                    />
                  </div>

                  <div>
                    <label className={`block text-xs font-semibold mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      Street Address <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={formData.address}
                      onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                      className={`w-full px-3 py-1.5 text-sm border rounded-lg transition-colors focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 ${
                        darkMode ? 'bg-[#121212] border-cyan-500/50 text-white placeholder-gray-500' : 'bg-white border-sky-400/40 text-gray-900'
                      }`}
                      placeholder="e.g., 4373 County Line Rd"
                      required
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <div>
                      <label className={`block text-xs font-semibold mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>City</label>
                      <input
                        type="text"
                        value={formData.city}
                        onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                        className={`w-full px-3 py-1.5 text-sm border rounded-lg transition-colors focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 ${
                          darkMode ? 'bg-[#121212] border-cyan-500/50 text-white placeholder-gray-500' : 'bg-white border-sky-400/40 text-gray-900'
                        }`}
                        placeholder="e.g., Chalfont"
                      />
                    </div>
                    <div>
                      <label className={`block text-xs font-semibold mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>State</label>
                      <input
                        type="text"
                        value={formData.state}
                        onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                        className={`w-full px-3 py-1.5 text-sm border rounded-lg transition-colors focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 ${
                          darkMode ? 'bg-[#121212] border-cyan-500/50 text-white placeholder-gray-500' : 'bg-white border-sky-400/40 text-gray-900'
                        }`}
                        placeholder="PA"
                        maxLength={2}
                      />
                    </div>
                    <div>
                      <label className={`block text-xs font-semibold mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>ZIP Code</label>
                      <input
                        type="text"
                        value={formData.zip}
                        onChange={(e) => setFormData({ ...formData, zip: e.target.value })}
                        className={`w-full px-3 py-1.5 text-sm border rounded-lg transition-colors focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 ${
                          darkMode ? 'bg-[#121212] border-cyan-500/50 text-white placeholder-gray-500' : 'bg-white border-sky-400/40 text-gray-900'
                        }`}
                        placeholder="18914"
                      />
                    </div>
                  </div>

                  <div>
                    <label className={`block text-xs font-semibold mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      Aliases (comma-separated)
                    </label>
                    <input
                      type="text"
                      value={formData.aliases}
                      onChange={(e) => setFormData({ ...formData, aliases: e.target.value })}
                      className={`w-full px-3 py-1.5 text-sm border rounded-lg transition-colors focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 ${
                        darkMode ? 'bg-[#121212] border-cyan-500/50 text-white placeholder-gray-500' : 'bg-white border-sky-400/40 text-gray-900'
                      }`}
                      placeholder="e.g., 4373 County, County Line Rd"
                    />
                    <p className={`text-[11px] mt-1 ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>Alternative street names or partial addresses</p>
                  </div>

                  {/* Facility Metadata Section */}
                  <div className={`pt-3 border-t ${darkMode ? 'border-cyan-500/30' : 'border-sky-400/30'}`}>
                    <div className={`inline-flex items-center gap-2 px-2 py-1 rounded-full text-xs font-semibold mb-3 ${
                      darkMode ? 'bg-cyan-500/20 text-cyan-300 border border-cyan-500/40' : 'bg-sky-50 text-sky-700 border border-sky-400/30'
                    }`}>
                      <BarChart3 className="h-3.5 w-3.5" /> Facility Metadata
                    </div>
                  
                    <div className="space-y-3">
                      <div>
                        <label className={`block text-xs font-semibold mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                          Total Square Footage
                        </label>
                        <input
                          type="number"
                          value={formData.squareFootage}
                          onChange={(e) => setFormData({ ...formData, squareFootage: e.target.value })}
                          className={`w-full px-3 py-1.5 text-sm border rounded-lg transition-colors focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 ${
                            darkMode ? 'bg-[#121212] border-cyan-500/50 text-white placeholder-gray-500' : 'bg-white border-sky-400/40 text-gray-900'
                          }`}
                          placeholder="e.g., 50000"
                          min="0"
                        />
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div>
                          <label className={`block text-xs font-semibold mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                            Office Space (sq ft)
                          </label>
                          <input
                            type="number"
                            value={formData.officeSquareFootage}
                            onChange={(e) => setFormData({ ...formData, officeSquareFootage: e.target.value })}
                            className={`w-full px-3 py-1.5 text-sm border rounded-lg transition-colors focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 ${
                              darkMode ? 'bg-[#121212] border-cyan-500/50 text-white placeholder-gray-500' : 'bg-white border-sky-400/40 text-gray-900'
                            }`}
                            placeholder="e.g., 5000"
                            min="0"
                          />
                        </div>
                        <div>
                          <label className={`block text-xs font-semibold mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                            Warehouse/Shop (sq ft)
                          </label>
                          <input
                            type="number"
                            value={formData.warehouseSquareFootage}
                            onChange={(e) => setFormData({ ...formData, warehouseSquareFootage: e.target.value })}
                            className={`w-full px-3 py-1.5 text-sm border rounded-lg transition-colors focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 ${
                              darkMode ? 'bg-[#121212] border-cyan-500/50 text-white placeholder-gray-500' : 'bg-white border-sky-400/40 text-gray-900'
                            }`}
                            placeholder="e.g., 45000"
                            min="0"
                          />
                        </div>
                      </div>

                      <div>
                        <label className={`block text-xs font-semibold mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                          Building Age (years)
                        </label>
                        <input
                          type="number"
                          value={formData.buildingAge}
                          onChange={(e) => setFormData({ ...formData, buildingAge: e.target.value })}
                          className={`w-full px-3 py-1.5 text-sm border rounded-lg transition-colors focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 ${
                            darkMode ? 'bg-[#121212] border-cyan-500/50 text-white placeholder-gray-500' : 'bg-white border-sky-400/40 text-gray-900'
                          }`}
                          placeholder="e.g., 25"
                          min="0"
                        />
                        <p className={`text-[11px] mt-1 ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>Helps AI contextualize construction standards</p>
                      </div>

                      <div>
                        <label className={`block text-xs font-semibold mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                          Notes for AI Analysis
                        </label>
                        <textarea
                          value={formData.notes}
                          onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                          rows={3}
                          className={`w-full px-3 py-1.5 text-sm border rounded-lg transition-colors focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 resize-vertical ${
                            darkMode ? 'bg-[#121212] border-cyan-500/50 text-white placeholder-gray-500' : 'bg-white border-sky-400/40 text-gray-900'
                          }`}
                          placeholder="e.g., Has EV chargers, solar panels installed 2024, cold storage warehouse, operates 24/7, seasonal operations..."
                        />
                        <p className={`text-[11px] mt-1 ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>Context provided to the AI during anomaly analysis (equipment, operations, known issues, etc.)</p>
                      </div>
                    </div>
                  </div>

                  {/* Facility Status Section */}
                  <div className={`pt-3 border-t ${darkMode ? 'border-cyan-500/30' : 'border-sky-400/30'}`}>
                    <div className={`inline-flex items-center gap-2 px-2 py-1 rounded-full text-xs font-semibold mb-3 ${
                      darkMode ? 'bg-cyan-500/20 text-cyan-300 border border-cyan-500/40' : 'bg-sky-50 text-sky-700 border border-sky-400/30'
                    }`}>
                      <Building2 className="h-3.5 w-3.5" /> Facility Status
                    </div>
                  
                    <div className="space-y-3">
                      <label className="flex items-center gap-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={formData.inactive}
                          onChange={(e) => setFormData({ ...formData, inactive: e.target.checked, inactiveDate: e.target.checked ? formData.inactiveDate : '' })}
                          className="rounded border-gray-300 text-cyan-500 focus:ring-cyan-500 cursor-pointer h-4 w-4"
                        />
                        <span className={`text-sm font-medium ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>Mark as Inactive</span>
                      </label>

                      {formData.inactive && (
                        <div>
                          <label className={`block text-xs font-semibold mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                            Inactive Effective Date
                          </label>
                          <input
                            type="month"
                            value={formData.inactiveDate}
                            onChange={(e) => setFormData({ ...formData, inactiveDate: e.target.value })}
                            className={`w-full px-3 py-1.5 text-sm border rounded-lg transition-colors focus:ring-2 focus:ring-red-500 focus:border-transparent ${
                              darkMode ? 'bg-[#121212] border-cyan-500/50 text-white' : 'bg-white border-sky-400/40 text-gray-900'
                            }`}
                          />
                          <p className={`text-[11px] mt-1 ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>Reports will exclude this facility for months after this date</p>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="flex gap-2 pt-2">
                    <button
                      type="submit"
                      className="flex-1 bg-cyan-500 text-white px-4 py-2 rounded-lg hover:bg-cyan-600 transition-colors font-medium shadow-[0_0_12px_rgba(14,165,233,0.4)]"
                    >
                      {editingId ? 'Update Location' : 'Add Location'}
                    </button>
                    {editingId && (
                      <button
                        type="button"
                        onClick={handleCancel}
                        className={`px-4 py-2 border rounded-lg transition-colors ${
                          darkMode ? 'border-cyan-500/50 text-gray-300 hover:bg-cyan-500/10' : 'border-sky-400/40 text-gray-700 hover:bg-sky-50'
                        }`}
                      >
                        Cancel
                      </button>
                    )}
                  </div>
                </form>
              </div>
            </div>

            {/* List - independently scrollable on desktop */}
            <div className="pb-20 md:pb-0 lg:overflow-y-auto lg:max-h-[calc(90vh-11rem)]" style={{ scrollbarGutter: 'stable' }}>
              {/* Unmatched Facilities Section */}
              {unmatchedFacilities.length > 0 && (
                <div className="mb-6">
                  <div className={`flex items-center gap-2 mb-3`}>
                    <AlertTriangle className={`w-4 h-4 ${darkMode ? 'text-amber-400' : 'text-amber-500'}`} />
                    <h3 className={`text-lg font-semibold ${darkMode ? 'text-amber-300' : 'text-amber-700'}`}>
                      Unmatched Sites ({unmatchedFacilities.length})
                    </h3>
                  </div>
                  <p className={`text-xs mb-3 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    These facility names were picked up by the AI from bills but don't match any approved location. Remove them to reassign their bills.
                  </p>
                  <div className="space-y-2">
                    {unmatchedFacilities.map(name => (
                      <div
                        key={name}
                        className={`border rounded-xl p-3 flex items-center justify-between transition-all duration-200 ${
                          darkMode
                            ? 'bg-amber-900/10 border-amber-500/30 shadow-[0_0_8px_rgba(245,158,11,0.1)]'
                            : 'bg-amber-50/50 border-amber-300/50 shadow-[0_1px_6px_rgba(245,158,11,0.08)]'
                        }`}
                      >
                        <div className="flex items-center gap-2 min-w-0">
                          <AlertTriangle className={`w-3.5 h-3.5 flex-shrink-0 ${darkMode ? 'text-amber-400' : 'text-amber-500'}`} />
                          <span className={`text-sm font-medium truncate ${darkMode ? 'text-amber-200' : 'text-amber-800'}`}>
                            {name}
                          </span>
                        </div>
                        <button
                          onClick={() => handleRemoveFacility(name)}
                          disabled={removingFacility === name}
                          className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all flex-shrink-0 ${
                            removingFacility === name
                              ? 'opacity-50 cursor-not-allowed'
                              : darkMode
                                ? 'bg-red-500/20 text-red-300 hover:bg-red-500/30 border border-red-500/40'
                                : 'bg-red-50 text-red-600 hover:bg-red-100 border border-red-300/50'
                          }`}
                          title={`Remove "${name}" and reassign its bills`}
                        >
                          {removingFacility === name ? (
                            <><Loader2 className="w-3 h-3 animate-spin" /> Removing...</>
                          ) : (
                            <><Trash className="w-3 h-3" /> Remove & Reassign</>
                          )}
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <h3 className={`text-lg font-semibold mb-4 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                Approved Locations ({locations.length})
              </h3>
              
              {loading ? (
                <div className={`text-center py-8 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Loading locations...</div>
              ) : locations.length === 0 ? (
                <div className={`text-center py-8 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  No locations yet. Add your first location to get started.
                </div>
              ) : (
                <div className="space-y-3">
                  {locations.map(location => (
                    <div
                      key={location.id}
                      className={`border rounded-xl p-4 transition-all duration-200 ${
                        darkMode
                          ? 'bg-[#1a1a2e] border-cyan-500/50 shadow-[0_0_10px_rgba(14,165,233,0.14)] hover:shadow-[0_0_15px_rgba(14,165,233,0.2)]'
                          : 'bg-white border-sky-400/30 shadow-[0_2px_10px_rgba(14,165,233,0.075)] hover:shadow-[0_4px_15px_rgba(14,165,233,0.125)]'
                      }`}
                    >
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <h4 className={`font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>{location.name}</h4>
                            {location.inactive && (
                              <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${
                                darkMode ? 'bg-red-900/30 text-red-400' : 'bg-red-100 text-red-700'
                              }`}>
                                Inactive{location.inactiveDate ? ` since ${location.inactiveDate}` : ''}
                              </span>
                            )}
                          </div>
                          <p className={`text-sm mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>{location.address}</p>
                          {location.city && (
                            <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                              {location.city}{location.state ? `, ${location.state}` : ''}{location.zip ? ` ${location.zip}` : ''}
                            </p>
                          )}
                          {(location.squareFootage || location.buildingAge) && (
                            <div className={`mt-2 flex flex-wrap gap-2 text-xs ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                              {location.squareFootage && (
                                <span className={`inline-flex items-center gap-1 px-2 py-1 rounded ${darkMode ? 'bg-cyan-500/10 border border-cyan-500/30' : 'bg-sky-50 border border-sky-400/30'}`}>
                                  <Maximize2 className="h-3 w-3" /> {location.squareFootage.toLocaleString()} sq ft
                                </span>
                              )}
                              {location.officeSquareFootage && (
                                <span className={`inline-flex items-center gap-1 px-2 py-1 rounded ${darkMode ? 'bg-cyan-500/10 border border-cyan-500/30' : 'bg-sky-50 border border-sky-400/30'}`}>
                                  <Building2 className="h-3 w-3" /> Office: {location.officeSquareFootage.toLocaleString()} sq ft
                                </span>
                              )}
                              {location.warehouseSquareFootage && (
                                <span className={`inline-flex items-center gap-1 px-2 py-1 rounded ${darkMode ? 'bg-cyan-500/10 border border-cyan-500/30' : 'bg-sky-50 border border-sky-400/30'}`}>
                                  <Warehouse className="h-3 w-3" /> Warehouse: {location.warehouseSquareFootage.toLocaleString()} sq ft
                                </span>
                              )}
                              {location.buildingAge && (
                                <span className={`inline-flex items-center gap-1 px-2 py-1 rounded ${darkMode ? 'bg-cyan-500/10 border border-cyan-500/30' : 'bg-sky-50 border border-sky-400/30'}`}>
                                  <HardHat className="h-3 w-3" /> {location.buildingAge} years old
                                </span>
                              )}
                            </div>
                          )}
                          {location.aliases && location.aliases.length > 0 && (
                            <div className="mt-2 flex flex-wrap gap-1">
                              {location.aliases.map((alias, idx) => (
                                <span
                                  key={idx}
                                  className={`text-xs px-2 py-1 rounded ${
                                    darkMode ? 'bg-cyan-500/20 text-cyan-300 border border-cyan-500/30' : 'bg-sky-50 text-sky-700 border border-sky-400/30'
                                  }`}
                                >
                                  {alias}
                                </span>
                              ))}
                            </div>
                          )}
                          {location.notes && (
                            <div className={`mt-2 text-xs px-3 py-2 rounded border-l-2 ${
                              darkMode ? 'bg-cyan-500/5 border-cyan-500 text-gray-300' : 'bg-sky-50 border-sky-400 text-gray-700'
                            }`}>
                              <span className={`font-semibold ${darkMode ? 'text-cyan-400' : 'text-sky-600'}`}>AI Notes:</span> {location.notes}
                            </div>
                          )}
                        </div>
                        <div className="flex gap-1 ml-4">
                          <button
                            onClick={() => handleEdit(location)}
                            className={`p-2 rounded transition-colors ${
                              darkMode ? 'text-cyan-400 hover:bg-cyan-500/20' : 'text-sky-600 hover:bg-sky-50'
                            }`}
                            title="Edit"
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(location.id)}
                            className={`p-2 rounded transition-colors ${
                              darkMode ? 'text-red-400 hover:bg-red-500/20' : 'text-red-600 hover:bg-red-50'
                            }`}
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className={`px-6 py-4 border-t transition-colors duration-200 ${
          darkMode ? 'bg-[#1a1a2e] border-cyan-500/50' : 'bg-white border-sky-400/30'
        }`}>
          <div className="flex items-center justify-between gap-4">
            <div className="relative">
              <button
                type="button"
                onClick={() => setShowTip(!showTip)}
                className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-semibold transition-colors shadow-sm ${
                  darkMode ? 'bg-[#121212] text-gray-100 hover:bg-cyan-500/10 border border-cyan-500/50' : 'bg-white text-gray-800 hover:bg-sky-50 border border-sky-400/30'
                }`}
                aria-expanded={showTip}
                aria-label="Show tip"
              >
                <span className="flex items-center justify-center h-5 w-5 rounded-full bg-yellow-400 text-yellow-900">
                  <Lightbulb className="h-3 w-3" />
                </span>
                <span>Tip</span>
              </button>
              {showTip && (
                <div className={`absolute left-0 bottom-full mb-2 w-80 rounded-lg shadow-lg p-3 text-sm ${
                  darkMode ? 'bg-[#1a1a2e] text-gray-100 border border-cyan-500/50' : 'bg-white text-gray-800 border border-sky-400/30'
                }`}
                  role="alert"
                >
                  <div className="font-semibold mb-1 flex items-center gap-2">
                    <span className="flex items-center justify-center h-5 w-5 rounded-full bg-yellow-400 text-yellow-900">
                      <Lightbulb className="h-3 w-3" />
                    </span>
                    Match Tips
                  </div>
                  <p>
                    When bills are uploaded, the AI matches extracted addresses to these approved locations. Add aliases for common street variations to improve matches.
                  </p>
                  <p className="mt-2">
                    If the AI picks up an address that isn't one of your locations, it will appear in the <strong>Unmatched Sites</strong> section above. Use "Remove & Reassign" to clean it up, or "Rebuild All Site Names" to re-match everything at once.
                  </p>
                </div>
              )}
            </div>
            <div className="flex items-center gap-2">
              {editingId && (
                <button
                  type="button"
                  onClick={handleCancel}
                  className={`px-4 py-2 border rounded-lg transition-colors ${
                    darkMode ? 'border-cyan-500/50 text-gray-300 hover:bg-cyan-500/10' : 'border-sky-400/40 text-gray-700 hover:bg-sky-50'
                  }`}
                >
                  Cancel
                </button>
              )}
              <button
                type="submit"
                form="location-form"
                className="bg-cyan-500 text-white px-4 py-2 rounded-lg hover:bg-cyan-600 transition-colors font-medium shadow-[0_0_12px_rgba(14,165,233,0.4)]"
              >
                {editingId ? 'Update Location' : 'Add Location'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
